"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Award, MapPin, ArrowRight, Building2, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Clapham() {
  const prefersReducedMotion = useReducedMotion();
  const highlights = [{
    title: "Victorian & Edwardian terraces",
    description: "Carefully positioned standards and cantilevered setups protect period facades and delicate cornicing."
  }, {
    title: "Converted flats & tight rear access",
    description: "Compact tube-and-fitting systems handled cleanly through narrow side passages and internal access routes."
  }, {
    title: "Pavements & busy shopfronts",
    description: "Full pedestrian gantries, high-visibility foam protection, debris netting, and council-approved licensing."
  }, {
    title: "Mixed-use commercial blocks",
    description: "Engineered access scaffolds that keep ground-floor businesses trading while upper floors undergo works."
  }];
  const animateProps = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="localintroclapham" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div {...animateProps} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="space-y-6">
          {/* Top badges */}
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge variant="outline" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-border bg-card">
              <MapPin className="size-3.5 text-primary" />
              Clapham &amp; South West London (SW4)
            </Badge>
            <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-medium">
              <Award className="size-3.5 text-primary" />
              20+ years trade leadership
            </Badge>
            <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-medium">
              <ShieldCheck className="size-3.5 text-primary" />
              NASC &amp; CHAS accredited
            </Badge>
          </div>

          {/* Heading and Primary Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-4">
              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
                Commercial and residential scaffolding in Clapham
              </h2>
              <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
                BMF Scaffolding Ltd provides dependable, safety-led commercial and residential scaffolding across Clapham, Clapham Common, Clapham Junction, and Greater London. Directed by Pete Wood with more than 20 years of hands-on industry experience, our fully qualified team delivers robust access solutions tailored to South West London&apos;s architectural landscape.
              </p>
              <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed">
                Whether you are refurbishing a multi-storey Victorian or Edwardian terrace, renovating converted flats with restricted rear access, or managing roofing works above active high-street shopfronts, we design compliant structures with full public protection, pavement licensing support, and minimal disruption to neighbours and passing pedestrians.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactRequestQuotePage">
                    Request a Clapham quote
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
                  <Link to="/AreasWeCoverPage">
                    <Building2 className="size-4 mr-1 text-muted-foreground" />
                    View all coverage areas
                  </Link>
                </Button>
              </div>
            </div>

            {/* Context highlights card */}
            <div className="lg:col-span-5">
              <Card className="border border-border bg-card shadow-sm">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-border">
                    <span className="font-default text-sm font-semibold text-foreground">
                      Clapham urban site expertise
                    </span>
                    <span className="text-xs text-muted-foreground">SW4 / SW11 / SW9</span>
                  </div>

                  <div className="space-y-3.5">
                    {highlights.map((item, index) => <div data-index={index} key={item.title} className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <h3 className="font-default text-sm font-semibold text-foreground">
                            {item.title}
                          </h3>
                        </div>
                        <p className="font-default text-xs text-muted-foreground pl-3.5 leading-relaxed">
                          {item.description}
                        </p>
                      </div>)}
                  </div>

                  <Separator className="my-3" />

                  <div className="bg-muted/50 rounded-md p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="size-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">Audited safety standards</span>
                    </div>
                    <span className="text-xs font-semibold text-primary">NASC &amp; CHAS</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}